import React, { useState } from 'react';
import { Shield, Check, ArrowRight, FileText, Search, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import PricingSection from '../components/PricingSection';
import RegisterPopup from '../components/RegisterPopup';
import ServiceProcessSection from '../components/ServiceProcessSection';
import SEO from '../components/SEO';
import ServiceFAQSection from '../components/ServiceFAQSection';

const TrademarkRegistration = () => {
  const [showPopup, setShowPopup] = useState(false);

  const benefits = [
    'Exclusive rights to use your trademark nationwide',
    'Legal protection against infringement',
    'Public notice of ownership claims',
    'Ability to register with U.S. Customs to prevent imports of infringing goods',
    'Foundation for international trademark protection',
    'Enhanced brand credibility and value'
  ];

  const processSteps = [
    {
      icon: 'FileText',
      title: 'Submit Your Information',
      description: 'Complete our simple form with your trademark details. Our team reviews your submission within 24 hours.'
    },
    {
      icon: 'Search',
      title: 'Comprehensive Search & Review',
      description: 'We conduct a thorough trademark search and analyze potential conflicts to ensure your application success.'
    },
    {
      icon: 'Award',
      title: 'Filing & Protection',
      description: 'We file your application with the USPTO and monitor it until your trademark is secured and protected.'
    }
  ];

  const icons = {
    FileText: FileText,
    Search: Search,
    Award: Award
  };

  const trademarkFAQs = [
    {
      question: 'How long does trademark registration take?',
      answer: 'The complete trademark registration process typically takes 8-12 months from filing to approval. We handle all communications with the USPTO and keep you updated throughout the entire process. Most applications receive an initial examination within 3-4 months.'
    },
    {
      question: 'What are the government filing fees?',
      answer: 'The USPTO charges a government filing fee of $250-$350 per class of goods/services. This fee is separate from our service fees and is paid directly to the government. We clearly disclose all fees before you proceed.'
    },
    {
      question: 'Can I trademark a name similar to an existing one?',
      answer: 'It depends on several factors including industry, geography, and degree of similarity. Our comprehensive search identifies potential conflicts and our experts provide guidance on your application\'s likelihood of success based on USPTO standards.'
    },
    {
      question: 'What happens if my application is rejected?',
      answer: 'Our packages include expert review to minimize rejection risk. If an office action is issued, our experienced team will work with you to craft an appropriate response. Premium packages include additional support for handling objections.'
    },
    {
      question: 'Do I need a lawyer to file a trademark?',
      answer: 'While not legally required, having professional assistance significantly increases your success rate. Our team includes USPTO-accredited specialists who ensure your application is properly prepared and filed, maximizing approval chances.'
    }
  ];

  return (
    <div>
      <SEO 
        title="Trademark Registration Services - Federal USPTO Filing | Trademark Flux"
        description="Professional trademark registration services with USPTO. Comprehensive search, application filing, and monitoring. Starting at $45. 98% success rate. Expert attorneys available."
        keywords="trademark registration, USPTO filing, federal trademark, trademark application, brand registration, trademark attorney"
      />
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(37, 99, 235, 0.7)), url('https://images.unsplash.com/photo-1742415888265-d5044039d8e6?q=80&w=2000')`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Trademark Registration Services
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Secure federal protection for your brand with expert trademark registration services. We make the process simple, fast, and affordable.
            </p>
            <Button
              onClick={() => setShowPopup(true)}
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg font-semibold"
            >
              Register Your Trademark Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* What is Trademark Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-aos="zoom-in" data-aos="fade-up">
            <div data-aos="fade-right">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is a Trademark?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                A trademark is a unique symbol, word, phrase, design, or combination that identifies and distinguishes your products or services from others in the marketplace. Federal trademark registration provides nationwide protection and exclusive rights to use your mark in connection with your goods or services.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Registering your trademark with the United States Patent and Trademark Office (USPTO) gives you legal presumption of ownership and the exclusive right to use the mark nationwide in connection with the goods and services listed in your registration.
              </p>
            </div>
            <div className="bg-blue-600 rounded-lg p-8 text-white" data-aos="zoom-in" data-aos="fade-left">
              <Shield className="h-16 w-16 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Protect Your Brand Identity</h3>
              <p className="mb-6">
                Don't risk losing your brand to competitors. Federal trademark registration provides the strongest legal protection available.
              </p>
              <Button
                onClick={() => setShowPopup(true)}
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                Get Started Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-aos="fade-up">
              Benefits of Federal Trademark Registration
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-aos="fade-up" data-aos="zoom-in">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start bg-white p-6 rounded-lg shadow-md" data-aos="zoom-in" data-aos-delay={index * 100}>
                  <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection onGetStarted={() => setShowPopup(true)} />

      {/* Process Section */}
      <ServiceProcessSection
        title="Our Simple 3-Step Process"
        subtitle="Get your trademark registered in three easy steps"
        steps={processSteps}
        icons={icons}
      />

      {/* FAQ Section */}
      <ServiceFAQSection
        faqs={trademarkFAQs}
        imageSrc="https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=800"
        onContactClick={() => setShowPopup(true)}
      />

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Secure Your Trademark?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Don't wait to protect your brand. Start your trademark registration today with our expert team.
          </p>
          <Button
            onClick={() => setShowPopup(true)}
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg"
          >
            Register My Trademark Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {showPopup && <RegisterPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default TrademarkRegistration;